import { database, firestore, storage } from '../firebase/config';
import { ref, push, set, update, get, serverTimestamp } from 'firebase/database';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import { doc, getDoc, getDocs, collection, query, where } from 'firebase/firestore';

class ChatService {
  // Helper function to convert storage path to download URL
  static async getImageUrl(imagePath) {
    // Handle null, undefined, or empty values
    if (!imagePath) {
      return null;
    }
    
    // Handle business user image structure: { path: "bussinessImage/...", type: "storageReference" }
    if (typeof imagePath === 'object') {
      if (imagePath.path && typeof imagePath.path === 'string') {
        imagePath = imagePath.path;
      } else {
        return null;
      }
    }
    
    // Ensure it's a string
    if (typeof imagePath !== 'string') {
      return null;
    }
    
    try {
      // Handle HTTP URLs
      if (imagePath.startsWith('http')) {
        return imagePath;
      }
      
      // Handle Firebase storage paths for both user types
      if (imagePath.startsWith('userProfilePhoto/') || imagePath.startsWith('bussinessImage/')) {
        return this.getFirebaseStorageUrl(imagePath);
      }
      
      return imagePath;
    } catch (error) {
      console.error('Error in getImageUrl:', error);
      return null;
    }
  }

  // Create or get existing chat room between two users
  static async createOrGetChatRoom(currentUserId, otherUserId) {
    try {
      // Create a consistent room ID (always smaller ID first)
      const roomId = currentUserId < otherUserId ? 
        `${currentUserId}_${otherUserId}` : 
        `${otherUserId}_${currentUserId}`;
      
      const chatRoomRef = ref(database, `chatRooms/${roomId}`);
      const snapshot = await get(chatRoomRef);
      
      if (!snapshot.exists()) {
        // Create new chat room
        const chatRoomData = {
          id: roomId,
          participants: {
            [currentUserId]: true,
            [otherUserId]: true
          },
          lastMessage: '',
          lastMessageTimestamp: serverTimestamp(),
          unreadCount: {
            [currentUserId]: 0,
            [otherUserId]: 0
          },
          createdAt: serverTimestamp()
        };
        
        await set(chatRoomRef, chatRoomData);
        console.log('Created new chat room:', roomId);
      }
      
      return roomId;
    } catch (error) {
      console.error('Error creating/getting chat room:', error);
      throw error;
    }
  }

  // Start a chat with a business (for job applications)
  static async startChatWithBusiness(currentUserId, businessId, jobId = null) {
    try {
      // Get business owner user ID
      const businessDoc = await getDoc(doc(firestore, 'businesses', businessId));
      if (!businessDoc.exists()) {
        throw new Error('Business not found');
      }
      
      const businessData = businessDoc.data();
      const businessOwnerId = businessData.managerUserId;
      
      if (!businessOwnerId) {
        throw new Error('Business owner not found');
      }
      
      const roomId = await this.createOrGetChatRoom(currentUserId, businessOwnerId);
      
      // Send initial message about job application if jobId is provided
      if (jobId) {
        const jobDoc = await getDoc(doc(firestore, 'jobs', jobId));
        if (jobDoc.exists()) {
          const jobData = jobDoc.data();
          const initialMessage = `Hi! I'm interested in the ${jobData.title} position at ${businessData.name}. I'd like to learn more about this opportunity.`;
          
          await this.sendMessage(roomId, currentUserId, initialMessage);
        }
      }
      
      return roomId;
    } catch (error) {
      console.error('Error starting chat with business:', error);
      throw error;
    }
  }

  // Send a message to a chat room
  static async sendMessage(roomId, senderId, text, fileUrl = null, fileName = null, fileType = null) {
    try {
      const messageData = {
        id: Date.now().toString(),
        senderId,
        content: {
          type: fileUrl ? (fileType?.startsWith('image/') ? 'image' : 'file') : 'text',
          text: text.trim(),
          fileUrl,
          fileName,
          fileType
        },
        timestamp: serverTimestamp(),
        // For compatibility with Firebase rules
        text: text.trim(),
        fileUrl,
        fileName,
        fileType
      };

      // Add message to chat room
      const messagesRef = ref(database, `chatRooms/${roomId}/messages`);
      await push(messagesRef, messageData);

      // Update last message in chat room
      const chatRoomRef = ref(database, `chatRooms/${roomId}`);
      const updateData = {
        lastMessage: text.trim() || (fileUrl ? `Sent a ${fileType?.startsWith('image/') ? 'photo' : 'file'}` : ''),
        lastMessageTimestamp: serverTimestamp()
      };

      // Get current participants to update unread counts
      const roomSnapshot = await get(chatRoomRef);
      if (roomSnapshot.exists()) {
        const roomData = roomSnapshot.val();
        const participants = roomData.participants || {};
        
        // Update unread count for other participants
        Object.keys(participants).forEach(participantId => {
          if (participantId !== senderId) {
            updateData[`unreadCount/${participantId}`] = (roomData.unreadCount?.[participantId] || 0) + 1;
          }
        });
      }

      await update(chatRoomRef, updateData);
      
      return messageData;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  // Mark messages as read for a user in a chat room
  static async markAsRead(roomId, userId) {
    try {
      const unreadCountRef = ref(database, `chatRooms/${roomId}/unreadCount/${userId}`);
      await set(unreadCountRef, 0);
    } catch (error) {
      console.error('Error marking messages as read:', error);
      throw error;
    }
  }

  // Set typing indicator
  static async setTyping(roomId, userId, isTyping) {
    try {
      const typingRef = ref(database, `chats/${roomId}/typing/${userId}`);
      await set(typingRef, isTyping);
    } catch (error) {
      console.error('Error setting typing indicator:', error);
      throw error;
    }
  }

  // Set user presence
  static async setUserPresence(userId, isOnline) {
    try {
      const presenceRef = ref(database, `users/${userId}/presence`);
      await set(presenceRef, {
        isOnline,
        lastOnline: serverTimestamp()
      });
    } catch (error) {
      console.error('Error setting user presence:', error);
      throw error;
    }
  }

  // Get user profile for chat display
  static async getUserProfile(userId) {
    try {
      const userDoc = await getDoc(doc(firestore, 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return {
          id: userId,
          name: userData.displayName || `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'Unknown User',
          avatar: userData.profilePhotoURL || null,
          businessId: userData.businessId,
          role: userData.role,
          email: userData.email
        };
      }
      return { id: userId, name: 'Unknown User', avatar: null };
    } catch (error) {
      console.error('Error getting user profile:', error);
      return { id: userId, name: 'Unknown User', avatar: null };
    }
  }

  // Get business profile for chat display
  static async getBusinessProfile(businessId) {
    try {
      const businessDoc = await getDoc(doc(firestore, 'businesses', businessId));
      if (businessDoc.exists()) {
        const businessData = businessDoc.data();
        return {
          id: businessId,
          name: businessData.name || 'Unknown Business',
          avatar: businessData.businessImage?.url || null,
          managerUserId: businessData.managerUserId,
          description: businessData.description
        };
      }
      return { id: businessId, name: 'Unknown Business', avatar: null };
    } catch (error) {
      console.error('Error getting business profile:', error);
      return { id: businessId, name: 'Unknown Business', avatar: null };
    }
  }

  // Search for users to start new conversations
  static async searchUsers(searchTerm, currentUserId) {
    try {
      const usersRef = collection(firestore, 'users');
      const usersSnapshot = await getDocs(usersRef);
      
      const allUsers = [];
      usersSnapshot.forEach((doc) => {
        const userData = doc.data();
        if (doc.id !== currentUserId) { // Exclude current user
          allUsers.push({
            id: doc.id,
            ...userData
          });
        }
      });
      
      // Filter users based on search term (search in name, email, business name)
      const filteredUsers = allUsers.filter(user => {
        const searchLower = searchTerm.toLowerCase();
        const firstName = (user.firstName || '').toLowerCase();
        const lastName = (user.lastName || '').toLowerCase();
        const email = (user.email || '').toLowerCase();
        const businessName = (user.businessName || '').toLowerCase();
        
        return firstName.includes(searchLower) || 
               lastName.includes(searchLower) || 
               email.includes(searchLower) ||
               businessName.includes(searchLower);
      });
      
      // Get image URLs for all users
      const usersWithImages = await Promise.all(
        filteredUsers.map(async (user) => {
          let imagePath = null;
          
          // Handle different image path structures
          if (user.role === 'business_user') {
            // Business users might have businessImage or profileImage
            if (user.businessImage && user.businessImage.path) {
              imagePath = user.businessImage.path;
            } else if (user.profileImage && user.profileImage.path) {
              imagePath = user.profileImage.path;
            } else if (user.profileImagePath) {
              imagePath = user.profileImagePath;
            }
          } else {
            // Regular users
            if (user.profileImagePath) {
              imagePath = user.profileImagePath;
            } else if (user.profileImage) {
              if (typeof user.profileImage === 'object' && user.profileImage.path) {
                imagePath = user.profileImage.path;
              } else if (typeof user.profileImage === 'string') {
                imagePath = user.profileImage;
              }
            }
          }
          
          const imageUrl = imagePath ? await this.getImageUrl(imagePath) : null;
          
          return {
            ...user,
            imageUrl,
            displayName: user.role === 'business_user' 
              ? (user.businessName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email)
              : `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
            userType: user.role === 'business_user' ? 'Business' : 'Job Seeker'
          };
        })
      );
      
      return usersWithImages;
    } catch (error) {
      console.error('Error searching users:', error);
      throw error;
    }
  }

  // Delete a chat room (admin only or if user is participant)
  static async deleteChatRoom(roomId, userId) {
    try {
      const chatRoomRef = ref(database, `chatRooms/${roomId}`);
      const snapshot = await get(chatRoomRef);
      
      if (snapshot.exists()) {
        const roomData = snapshot.val();
        if (roomData.participants && roomData.participants[userId]) {
          // Remove the entire chat room
          await set(chatRoomRef, null);
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error('Error deleting chat room:', error);
      throw error;
    }
  }
}

export default ChatService; 